<template>
  <v-card flat color="transparent">
    <!-- Top Action Bar (Admins / Editable Mode) -->
    <v-card-text class="pa-0 mb-4 mx-4" v-if="mode">
      <v-row align="center" justify="space-between" dense>
        <v-col cols="12" sm="auto" class="d-flex align-center">
          <!-- Toggle Edit Mode -->
          <v-btn
            v-if="!isMobile"
            outlined
            :color="isEditingMode ? 'amber darken-2' : 'primary'"
            dark
            depressed
            class="rounded-lg mr-2"
            @click="isEditingMode = !isEditingMode"
          >
            <v-icon left>
              {{ isEditingMode ? "mdi-eye" : "mdi-file-document-edit-outline" }}
            </v-icon>
            {{ isEditingMode ? "View Mode" : "Enable Edit" }}
          </v-btn>

          <!-- Add Item Button -->
          <v-btn
            v-if="showAddButton"
            outlined
            color="success"
            depressed
            class="rounded-lg"
            @click="openModal('add')"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            {{ isMobile ? "Add New" : `Add ${formatName(table)}` }}
          </v-btn>
        </v-col>

        <v-col cols="12" sm="4" md="3">
          <!-- Search Field -->
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search..."
            single-line
            hide-details
            outlined
            dense
            class="rounded-lg mx-4"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Loading State -->
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-2"
    ></v-progress-linear>

    <!-- 1. MOBILE CARD VIEW (Responsive < sm) -->
    <div v-if="$vuetify.breakpoint.xsOnly" class="mobile-card-container px-4">
      <!-- Search on Mobile if view-only mode -->
      <v-text-field
        v-if="!mode"
        v-model="search"
        append-icon="mdi-magnify"
        label="Search items..."
        outlined
        dense
        hide-details
        class="mb-4 rounded-xl px-4"
      ></v-text-field>

      <v-row dense>
        <v-col
          v-for="(item, index) in filteredItems"
          :key="item.id || index"
          cols="12"
        >
          <v-card elevation="2" class="rounded-xl mb-3 pa-2 mobile-card">
            <!-- Header Section: Primary Name/Label + Action Icons -->
            <div class="d-flex justify-space-between align-center px-3 pt-2">
              <div
                class="text-subtitle-1 font-weight-bold primary--text text-truncate"
              >
                {{ item.name || item.title || item.description || "Item" }}
              </div>

              <!-- Delete/Edit Actions for Mobile -->
              <div v-if="mode" class="d-flex align-center">
                <v-btn
                  icon
                  small
                  color="primary"
                  class="mr-1"
                  @click.stop="openModal('edit', item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="error"
                  @click.stop="openModal('delete', item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>

            <v-divider class="my-2"></v-divider>

            <!-- Card Body: Regular Field Badges -->
            <v-card-text class="py-1 px-3">
              <v-row dense>
                <template v-for="col in regularColumns">
                  <v-col
                    :key="col"
                    cols="6"
                    v-if="item[col] !== undefined && item[col] !== null"
                    class="py-1"
                  >
                    <div class="caption grey--text text--darken-1">
                      {{ formatLabel(col) }}
                    </div>
                    <div class="body-2 font-weight-medium text-truncate">
                      <!-- Render relational dropdown values if available -->
                      {{ getRelationValue(col, item[col]) }}
                    </div>
                  </v-col>
                </template>
              </v-row>
            </v-card-text>

            <!-- Card Footer: Money / Financial Columns -->
            <div
              v-if="moneyColumns.length > 0"
              class="mobile-money-footer px-3 py-2 rounded-lg mt-2 d-flex flex-wrap justify-space-between align-center"
            >
              <template v-for="mCol in nonTotalMoneyColumns">
                <div
                  :key="mCol"
                  v-if="item[mCol] !== undefined"
                  class="d-flex flex-column my-1"
                >
                  <span class="caption grey--text text--darken-2">{{
                    formatLabel(mCol)
                  }}</span>
                  <span class="body-2 font-weight-bold">
                    {{ formatCurrency(item[mCol]) }}
                  </span>
                </div>
              </template>

              <!-- Total Column Prominently Displayed Bottom-Right -->
              <div
                v-if="totalMoneyColumn && item[totalMoneyColumn] !== undefined"
                class="ml-auto text-right my-1"
              >
                <span class="caption primary--text font-weight-bold d-block">
                  {{ formatLabel(totalMoneyColumn) }}
                </span>
                <span class="text-h6 font-weight-black success--text">
                  {{ formatCurrency(item[totalMoneyColumn]) }}
                </span>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Empty State -->
        <v-col cols="12" v-if="filteredItems.length === 0">
          <v-alert type="info" text class="rounded-xl text-center">
            No records found.
          </v-alert>
        </v-col>
      </v-row>
    </div>

    <!-- 2. DESKTOP / TABLET DATA TABLE (Responsive >= sm) -->
    <v-data-table
      v-else
      :headers="computedTableHeaders"
      :items="items"
      :search="search"
      :loading="loading"
      class="elevation-1 rounded-lg excel-table"
      :class="{ 'editing-active': isEditingMode }"
      dense
    >
      <!-- Dynamic Slot Generator for Table Columns -->
      <template
        v-for="header in activeColumns"
        v-slot:[`item.${header}`]="{ item }"
      >
        <div :key="header" class="cell-wrapper">
          <!-- INLINE EXCEL EDITING MODE -->
          <template v-if="mode && isEditingMode">
            <!-- Relational Dropdown -->
            <v-select
              v-if="relations[header]"
              v-model="item[header]"
              :items="getRelationOptions(header)"
              item-text="name"
              item-value="id"
              dense
              hide-details
              outlined
              class="excel-input"
              @change="saveInlineCell(item)"
            ></v-select>

            <!-- Date Picker -->
            <v-menu
              v-else-if="isDateColumn(header)"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="item[header]"
                  dense
                  hide-details
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  class="excel-input"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="item[header]"
                no-title
                @input="saveInlineCell(item)"
              ></v-date-picker>
            </v-menu>

            <!-- Standard Text / Numeric Input -->
            <v-text-field
              v-else
              v-model="item[header]"
              dense
              hide-details
              outlined
              class="excel-input"
              :class="{ 'text-right': isMoneyColumn(header) }"
              @blur="saveInlineCell(item)"
              @keyup.enter="saveInlineCell(item)"
            ></v-text-field>
          </template>

          <!-- VIEW MODE -->
          <template v-else>
            <span
              v-if="isMoneyColumn(header)"
              class="font-weight-bold success--text d-block text-right"
            >
              {{ formatCurrency(item[header]) }}
            </span>
            <span v-else>
              {{ getRelationValue(header, item[header]) }}
            </span>
          </template>
        </div>
      </template>

      <!-- Row Action Buttons (Edit / Delete Dialog Launchers) -->
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-center">
          <v-btn
            icon
            small
            color="primary"
            class="mr-1"
            @click="openModal('edit', item)"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="openModal('delete', item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- 3. ADD / EDIT / DELETE MODAL DIALOG -->
    <v-dialog v-model="dialog" max-width="500px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline grey lighten-2 text-capitalize">
          {{ dialogMode }} {{ formatName(table) }}
        </v-card-title>

        <v-card-text class="mt-4">
          <v-container v-if="dialogMode !== 'delete'">
            <v-row dense>
              <v-col v-for="col in activeColumns" :key="col" cols="12">
                <!-- Relation Dropdown -->
                <v-select
                  v-if="relations[col]"
                  v-model="editedItem[col]"
                  :items="getRelationOptions(col)"
                  item-text="name"
                  item-value="id"
                  :label="formatLabel(col)"
                  outlined
                  dense
                ></v-select>

                <!-- Date Field -->
                <v-menu
                  v-else-if="isDateColumn(col)"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="editedItem[col]"
                      :label="formatLabel(col)"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="editedItem[col]"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>

                <!-- Color Picker -->
                <template v-else-if="isColorColumn(col)">
                  <p class="mb-1 grey--text text--darken-1">
                    {{ formatLabel(col) }}
                  </p>
                  <v-color-picker
                    v-model="editedItem[col]"
                    flat
                    dot-size="25"
                    swatches-max-height="200"
                    class="mb-4"
                  ></v-color-picker>
                </template>

                <!-- Default Input -->
                <v-text-field
                  v-else
                  v-model="editedItem[col]"
                  :label="formatLabel(col)"
                  :prefix="isMoneyColumn(col) ? '$' : ''"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>

          <v-alert v-else type="warning" text class="rounded-lg mb-0">
            Are you sure you want to delete this record?
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Cancel</v-btn>
          <v-btn
            :color="dialogMode === 'delete' ? 'error' : 'success'"
            depressed
            :loading="saving"
            @click="saveModalChange"
          >
            {{ dialogMode === "delete" ? "Confirm Delete" : "Save Changes" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "@/plugins/axios";
import helpersJs from "@/plugins/helpers";

export default {
  name: "CrudEditableTable",
  props: {
    table: {
      type: String,
      required: true,
    },
    tableItems: {
      type: Array,
      default: () => [],
    },
    tableColumns: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    moneyColumns: {
      type: Array,
      default: () => ["total", "price", "fee", "amount", "cost"],
    },
    showAddButton: {
      type: Boolean,
      default: true,
    },
    hiddenColumns: {
      type: Array,
      default: () => [
        "id",
        "password",
        "created_at",
        "updated_at",
        "deleted_at",
      ],
    },
    mode: {
      type: Boolean,
      default: true,
    },
    triggerRefresh: {
      type: Number,
      default: 0,
    },
    whereCol: {
      default: null,
    },
    whereVal: {
      default: null,
    },
  },
  data: () => ({
    items: [],
    columns: [],
    relations: {},
    loading: false,
    saving: false,
    search: "",
    isEditingMode: false,
    dialog: false,
    dialogMode: "add",
    editedItem: {},
  }),
  computed: {
    getAuthenticated() {
      return this.$store.getters.getAuthenticated;
    },
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getCurrency() {
      return this.$store.getters.getCurrency;
    },
    getAccessToken() {
      return this.$store.getters.getAccessToken;
    },
    // Filter active columns excluding hidden ones
    activeColumns() {
      const sourceCols = this.headers.length > 0 ? this.headers : this.columns;
      return sourceCols.filter((col) => !this.hiddenColumns.includes(col));
    },
    // Non-money columns for mobile card content
    regularColumns() {
      return this.activeColumns.filter(
        (col) => col !== "name" && col !== "title" && !this.isMoneyColumn(col)
      );
    },
    // Non-total money columns
    nonTotalMoneyColumns() {
      return this.moneyColumns.filter(
        (col) =>
          this.activeColumns.includes(col) && col.toLowerCase() !== "total"
      );
    },
    // Identifies primary total money column
    totalMoneyColumn() {
      if (
        this.moneyColumns.includes("total") &&
        this.activeColumns.includes("total")
      ) {
        return "total";
      }
      return (
        this.moneyColumns.find((col) => this.activeColumns.includes(col)) ||
        null
      );
    },
    // Table Headers for Vuetify Desktop Data Table
    computedTableHeaders() {
      const formatted = this.activeColumns.map((col) => ({
        text: this.formatLabel(col),
        value: col,
        align: this.isMoneyColumn(col) ? "end" : "start",
        sortable: true,
      }));

      if (this.mode) {
        formatted.unshift({
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "center",
          width: "100px",
        });
      }
      return formatted;
    },
    // Filtered items for mobile view search filtering
    filteredItems() {
      if (!this.search) return this.items;
      const term = this.search.toLowerCase();
      return this.items.filter((item) =>
        Object.values(item).some((val) =>
          String(val || "")
            .toLowerCase()
            .includes(term)
        )
      );
    },
  },
  watch: {
    table: {
      handler() {
        this.fetchTableData();
      },
      immediate: true,
    },
    tableItems: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.setTableItems(newVal);
      },
    },
    headers: {
      handler() {
        if (this.items.length === 0) this.fetchTableData();
      },
      deep: true,
    },
    triggerRefresh: {
      handler() {
        this.fetchTableData();
      },
    },
  },
  methods: {
    async setTableItems(tableItems) {
      if (tableItems.length < 1) {
        await this.fetchTableData();
      } else {
        this.items = tableItems;
        this.columns = this.tableColumns || [];
        this.relations = {};
      }
    },
    async fetchTableData() {
      if (!this.getAuthenticated || !this.getAccessToken || !this.table) return;

      this.loading = true;
      try {
        const res = await axios.postDataWithToken(
          "/auth/get-table-items-with-table-name",
          { table: this.table, wc: this.whereCol, wv: this.whereVal },
          this.getAccessToken
        );

        if (res && res.status === "success") {
          this.items = res.items || [];
          this.columns = res.columns || [];
          this.relations = res.relations || {};
        }
      } catch (e) {
        console.error("Failed to load table items:", e);
      } finally {
        this.loading = false;
      }
    },

    async saveInlineCell(item) {
      if (!this.mode) return;
      try {
        await axios.postDataWithToken(
          "/auth/add-edit-delete-autocomplete",
          {
            ...item,
            table: this.table,
            mode: "edit",
          },
          this.getAccessToken
        );
      } catch (e) {
        console.error("Inline save failed:", e);
        this.fetchTableData(); // Revert on error
      }
    },

    openModal(mode, item = null) {
      this.dialogMode = mode;
      if (item) {
        this.editedItem = JSON.parse(JSON.stringify(item));
      } else {
        const newItem = {};
        this.activeColumns.forEach((col) => (newItem[col] = ""));
        this.editedItem = newItem;
      }
      this.dialog = true;
    },

    async saveModalChange() {
      this.saving = true;
      try {
        const dataToSave = { ...this.editedItem };

        // Convert color objects to hex strings
        Object.keys(dataToSave).forEach((key) => {
          if (this.isColorColumn(key) && typeof dataToSave[key] === "object") {
            dataToSave[key] = dataToSave[key].hex;
          }
        });

        const res = await axios.postDataWithToken(
          "/auth/add-edit-delete-autocomplete",
          {
            ...dataToSave,
            table: this.table,
            mode: this.dialogMode,
          },
          this.getAccessToken
        );

        if (res && res.status === "success") {
          await this.fetchTableData();
          this.dialog = false;
        }
      } catch (e) {
        console.error("Save dialog action failed:", e);
      } finally {
        this.saving = false;
      }
    },

    // Helpers & Utilities
    formatName(name) {
      return helpersJs?.formatUnderscoreNamesWithFirstCapitalLetters
        ? helpersJs.formatUnderscoreNamesWithFirstCapitalLetters(name)
        : name;
    },

    formatLabel(col) {
      if (col === "dob") return "Date of Birth";
      return col.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },

    isMoneyColumn(col) {
      return this.moneyColumns.some(
        (m) => m.toLowerCase() === col.toLowerCase()
      );
    },

    isDateColumn(col) {
      const dateTerms = ["birthday", "dob", "expiry", "start", "end"];
      const lower = col.toLowerCase();
      return lower.includes("date") || dateTerms.includes(lower);
    },

    isColorColumn(col) {
      return col.toLowerCase().includes("color");
    },

    formatCurrency(val) {
      if (val === null || val === undefined || val === "") return "-";
      const num = Number(val);
      if (isNaN(num)) return val;
      return this.getCurrency + helpersJs.formatMoney(num);
    },

    getRelationOptions(col) {
      if (!this.relations[col]) return [];
      return this.relations[col].items || this.relations[col] || [];
    },

    getRelationValue(col, val) {
      if (!this.relations[col]) return val;
      const list = this.getRelationOptions(col);
      const match = list.find((r) => r.id === val);
      return match ? match.name : val;
    },
  },
};
</script>

<style scoped>
/* Excel-like Table Styling */
.excel-table.editing-active ::v-deep td {
  padding: 2px 4px !important;
  border: 1px solid #e0e0e0 !important;
}

.excel-input ::v-deep .v-input__control {
  min-height: 32px !important;
}

.excel-input ::v-deep .v-input__slot {
  min-height: 32px !important;
  padding: 0 6px !important;
  background-color: #fbfbfb !important;
}

/* Mobile Styling */
.mobile-card {
  border: 1px solid #f0f0f0;
  transition: transform 0.15s ease-in-out;
}

.mobile-card:active {
  transform: scale(0.99);
}

.mobile-money-footer {
  background-color: #f8f9fa;
  border-top: 1px dashed #e0e0e0;
}
</style>
